import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Checkbox, Form, Message, Modal } from "semantic-ui-react";
import { allowDs82BotReprocessApi, getApplicantLogsApi } from "../../../../../../../../apis/applicantApi";
import { APPLICANT_STATUSES } from "../../../constants/applicantStatuses";

const manualStatus = APPLICANT_STATUSES["Processed manually"];
const hasManualHistory = (logs) => logs.some((log) => log.status === manualStatus && log.botGuardIgnored !== true);

const historyLoadError = "Could not load status history. Reload this applicant.";

export default function Ds82Reprocess({ applicantId, status, onQueued, onRefreshApplicantStatus, onPendingChange }) {
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    let canceled = false;
    setEligible(false);
    getApplicantLogsApi(applicantId, "ds82")
      .then(({ data }) => { if (!canceled) setEligible(hasManualHistory(data)); })
      .catch(() => {
        if (!canceled) {
          setError((previous) => previous.includes(historyLoadError)
            ? previous
            : [previous, historyLoadError].filter(Boolean).join(" "));
        }
      });
    return () => { canceled = true; };
  }, [applicantId, status]);

  const close = () => {
    if (submitting.current) return;
    setOpen(false);
    setReason("");
    setAcknowledged(false);
  };
  const confirm = async () => {
    if (submitting.current || !eligible || status === manualStatus || !reason.trim() || !acknowledged) return;
    submitting.current = true;
    setPending(true);
    onPendingChange(applicantId, true);
    setError("");
    let queued = false;
    try {
      await allowDs82BotReprocessApi(applicantId, reason.trim());
      queued = true;
      if (mounted.current) {
        setEligible(false);
        onQueued(applicantId);
      }
    } catch (failure) {
      if (mounted.current) setError(failure.response?.data?.message || "The bot run could not be confirmed. Check the applicant before trying again.");
    } finally {
      try {
        const [{ data }] = await Promise.all([
          getApplicantLogsApi(applicantId, "ds82"),
          onRefreshApplicantStatus(applicantId),
        ]);
        if (mounted.current) setEligible(hasManualHistory(data));
      } catch (_) {
        if (mounted.current) {
          setEligible(false);
          setError((previous) => `${previous || "Could not refresh the applicant."} Reload this applicant to check its current status and history.`);
        }
      }
      if (mounted.current && queued) {
        setOpen(false);
        setReason("");
        setAcknowledged(false);
      }
      submitting.current = false;
      if (mounted.current) setPending(false);
      onPendingChange(applicantId, false);
    }
  };

  return (
    <div className="ds82-reprocess">
      {eligible && <Button color="orange" onClick={() => { setError(""); setOpen(true); }}>Allow bot to reprocess</Button>}
      {error && !open && <Message negative role="alert">{error}</Message>}
      <Modal className="ds82-reprocess-modal" size="small" open={open} onClose={close} closeOnEscape={!pending} closeOnDimmerClick={!pending}>
        <Modal.Header>Allow bot to reprocess DS82 renewal</Modal.Header>
        <Modal.Content scrolling>
          <Message warning>
            <Message.Header>Check for a completed government submission or payment</Message.Header>
            <p>Running again can create a duplicate submission or charge. Check the government account and payment evidence before confirming.</p>
            <p>This ignores existing Processed manually history and starts a normal bot run. All other bot checks still apply.</p>
          </Message>
          {status === manualStatus && <Message warning>Save a current status other than Processed manually before running the bot.</Message>}
          <Form>
            <Form.TextArea id="ds82-reprocess-reason" label="Reason for reprocessing" required value={reason} disabled={pending} onChange={(_, data) => setReason(data.value)} />
            <Form.Field required>
              <Checkbox label="I checked that no government submission or government payment was completed." checked={acknowledged} disabled={pending} onChange={(_, data) => setAcknowledged(data.checked)} />
            </Form.Field>
          </Form>
          {error && <Message negative role="alert">{error}</Message>}
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={pending} onClick={close}>Cancel</Button>
          <Button color="orange" loading={pending} disabled={pending || !eligible || status === manualStatus || !reason.trim() || !acknowledged} onClick={confirm}>Confirm and run bot</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}

Ds82Reprocess.propTypes = {
  applicantId: PropTypes.string.isRequired,
  status: PropTypes.number.isRequired,
  onQueued: PropTypes.func.isRequired,
  onRefreshApplicantStatus: PropTypes.func.isRequired,
  onPendingChange: PropTypes.func.isRequired,
};
